// src/Dashboard/Clients.js
import React, { useState, useEffect } from "react";
import api from "./Api";
import "./Address.css"; // 🟢 نستخدم نفس الاستايل統一 الشكل

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({ name: "", logo: null });
  const [editingClient, setEditingClient] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // 🔎 search state

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/api/Client/GetAll");
      if (Array.isArray(response.data)) {
        setClients(response.data);
      } else if (response.data?.$values) {
        setClients(response.data.$values);
      } else {
        setClients([]);
      }
    } catch (err) {
      setError("Failed to fetch clients.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, name: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, logo: file });
    setImagePreview(file ? URL.createObjectURL(file) : null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("name", formData.name);
    if (formData.logo) fd.append("logo", formData.logo);

    try {
      setLoading(true);
      if (editingClient) {
        const response = await api.put(
          `/admin/api/Client/Update/${editingClient.id}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        const updatedClient = response.data;
        setClients((prev) =>
          prev.map((c) => (c.id === editingClient.id ? updatedClient : c))
        );
        setSuccess("Client updated successfully!");
      } else {
        const response = await api.post("/admin/api/Client/Create", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setClients((prev) => [...prev, response.data]);
        setSuccess("Client added successfully!");
      }
      resetForm();
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Failed to save client.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({ name: client.name, logo: null });
    setImagePreview(client.logoUrl || null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try {
      setLoading(true);
      await api.delete(`/admin/api/Client/Delete/${id}`);
      setClients((prev) => prev.filter((c) => c.id !== id));
      setSuccess("Client deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Failed to delete client.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({ name: "", logo: null });
    setImagePreview(null);
  };

  // 🔎 فلترة العملاء حسب البحث
  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="address-container">
      <div className="dashboard-header">
        <h1>Clients Management Dashboard</h1>
        <p>Manage all clients in the system</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Form Card */}
      <div className="card">
        <div className="card-header">
          <h3>{editingClient ? "Edit Client" : "Add New Client"}</h3>
        </div>
        <div className="card-body">
          <form
            className="address-form"
            onSubmit={handleSubmit}
            encType="multipart/form-data"
          >
            <div className="form-group">
              <label htmlFor="name">Client Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter client name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="logo">Logo</label>
              <input
                type="file"
                id="logo"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: "120px",
                    maxHeight: "120px",
                    marginTop: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    padding: "4px",
                  }}
                />
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {editingClient ? "Update" : "Add"}
              </button>
              {editingClient && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* List Card */}
      <div className="card">
        <div className="card-header">
          <h3>Clients List</h3>
          <div >
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <span className="badge">{clients.length}</span>
        </div>
        <div className="card-body">
          {loading && clients.length === 0 ? (
            <p>Loading...</p>
          ) : clients.length === 0 ? (
            <p>No clients found.</p>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Logo</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client, index) => (
                    <tr key={client.id}>
                      <td>{index + 1}</td>
                      <td>
                        {client.logoUrl && (
                          <img
                            src={client.logoUrl}
                            alt={client.name}
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "contain",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              padding: "2px",
                              background: "#fff",
                            }}
                          />
                        )}
                      </td>
                      <td>{client.name}</td>
                      <td>
                        <button
                          onClick={() => handleEdit(client)}
                          className="btn-edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(client.id)}
                          className="btn-danger"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Clients;
